import { useTranslation } from 'react-i18next';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

export default function TransWomenRightsLuxembourg() {
  const { t } = useTranslation('blog');

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
              <span className="px-3 py-1 bg-purple-50 text-purple-600 rounded-full font-medium">Gender Rights</span>
              <span>•</span>
              <time>March 15, 2024</time>
              <span>•</span>
              <span>8 min read</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Trans Women's Rights in Luxembourg: Progress, Challenges, and the Road Ahead
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              An in-depth look at the current state of transgender rights in Luxembourg, recent legislative changes, and what still needs to be done to ensure full equality and protection for trans women.
            </p>
          </div>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img 
              src="https://public.readdy.ai/ai/img_res/f6de34a4b677e9fb305141efde8cb3ad.jpg"
              alt="Trans rights activists in Luxembourg"
              className="w-full h-[400px] object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Luxembourg's Progressive Stance</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Luxembourg has positioned itself as one of Europe's more progressive nations regarding LGBTQ+ rights, but the journey for trans women's rights specifically has been complex and ongoing. While the country legalized same-sex marriage in 2015 and has strong anti-discrimination laws, trans-specific protections have evolved more gradually.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Recent Legislative Victories</h2>
            
            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Gender Recognition Law (2018)</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              In 2018, Luxembourg passed groundbreaking legislation allowing individuals to change their legal gender through a simple administrative procedure. This law eliminated the previous requirements for medical intervention, psychiatric evaluation, or sterilization - requirements that were rightly criticized as human rights violations.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Key provisions include:</strong>
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Self-determination of gender identity without medical requirements</li>
              <li>Simplified name change procedures</li>
              <li>Protection of privacy regarding gender history</li>
              <li>Access for minors with parental consent (or court approval if parents disagree)</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Healthcare Access Improvements</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The Luxembourg healthcare system has made strides in covering gender-affirming care, including hormone therapy and surgical procedures. However, access remains inconsistent, with long waiting times and limited specialized providers creating barriers for many trans women.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Ongoing Challenges</h2>

            <div className="bg-rose-50 border-l-4 border-rose-400 p-6 my-8 rounded-r-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Employment Discrimination</h3>
              <p className="text-gray-700 leading-relaxed">
                Despite legal protections, trans women in Luxembourg still face significant workplace discrimination. A 2023 survey by Rosa Lëtzebuerg found that 42% of trans respondents had experienced discrimination in job applications or workplace settings.
              </p>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Healthcare Gaps</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              While gender-affirming care is theoretically covered, practical barriers remain:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Limited number of specialized healthcare providers</li>
              <li>Waiting times of 12-18 months for initial consultations</li>
              <li>Lack of mental health support specifically trained in trans issues</li>
              <li>Inconsistent coverage decisions by insurance providers</li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Social Acceptance</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              While Luxembourg's urban centers are generally accepting, trans women still face harassment, misgendering, and social exclusion. Rural areas present additional challenges, with less visibility and fewer support networks.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Violence and Safety Concerns</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Trans women, particularly trans women of color, face disproportionate rates of violence and harassment. While Luxembourg has relatively low rates of hate crimes compared to other European nations, underreporting remains a significant issue. Many trans women don't report incidents due to fear of being outed, previous negative experiences with authorities, or lack of faith in the justice system.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What Needs to Change</h2>

            <div className="bg-purple-50 p-8 rounded-2xl my-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Priority Actions</h3>
              <ol className="list-decimal pl-6 text-gray-700 space-y-3">
                <li><strong>Expand healthcare infrastructure:</strong> Increase the number of trained providers and reduce waiting times</li>
                <li><strong>Strengthen workplace protections:</strong> Implement mandatory trans-inclusive policies in all organizations</li>
                <li><strong>Education and awareness:</strong> Include trans history and issues in school curricula</li>
                <li><strong>Support services:</strong> Fund community organizations providing support to trans individuals</li>
                <li><strong>Data collection:</strong> Improve tracking of discrimination and violence to inform policy</li>
              </ol>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Community Voices</h2>
            <blockquote className="border-l-4 border-purple-400 pl-6 italic text-gray-700 my-8 text-lg">
              "The legal changes were important, but they're just the beginning. We need society to catch up with the law. We need employers who don't just tolerate us but value us. We need healthcare providers who understand our needs. We need to be seen as the women we are."
              <footer className="text-gray-600 mt-3 not-italic">— Sophie, trans woman living in Luxembourg City</footer>
            </blockquote>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How You Can Support Trans Women's Rights</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Educate yourself about trans issues and experiences</li>
              <li>Use correct names and pronouns - it matters more than you think</li>
              <li>Support trans-led organizations like Rosa Lëtzebuerg and CIGALE</li>
              <li>Speak up against transphobia when you witness it</li>
              <li>Advocate for trans-inclusive policies in your workplace and community</li>
              <li>Donate to organizations providing direct support to trans individuals</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Looking Forward</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Luxembourg has made important progress, but the work is far from complete. Trans women deserve not just legal recognition, but full social acceptance, economic opportunity, and safety. This requires ongoing commitment from policymakers, institutions, and individuals.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              The trans rights movement in Luxembourg is growing stronger, with more visibility, more allies, and more determination than ever before. By standing together and continuing to push for change, we can create a Luxembourg where every trans woman can live authentically, safely, and with dignity.
            </p>

            <div className="bg-gray-50 p-8 rounded-2xl mt-12">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Resources</h3>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Rosa Lëtzebuerg:</strong> Luxembourg's LGBTQ+ rights organization</li>
                <li><strong>CIGALE:</strong> Support group for trans and gender-diverse individuals</li>
                <li><strong>Centre LGBTIQ+ CIGALE:</strong> Community center offering counseling and support</li>
                <li><strong>Planning Familial:</strong> Sexual and reproductive health services including trans healthcare</li>
              </ul>
            </div>
          </div>

          {/* Author */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                <span className="text-white text-xl font-bold">SC</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">SheCan Editorial Team</p>
                <p className="text-gray-600 text-sm">Advocating for gender equality and trans rights</p>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}